import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { MatchConfig } from '../../core/contracts';
import { prng } from './util';

/**
 * ~30k fans, instanced and animated entirely in the vertex shader (idle sway, bounce,
 * arm-raising/waving on excitement); the CPU only updates a few uniforms per frame.
 *
 * Close-range quality without paying for it everywhere: the stands are split into
 * spatial chunks, each with two InstancedMeshes sharing the same per-fan buffers —
 *  - FAR: a few boxes per fan (~40 tris), used for the wide shots / monitor wall;
 *  - NEAR: a lofted silhouette (shoulders, neck, rounded head, separate legs, arms with
 *    hands, optional scarf) used only for chunks where a fan is tall on screen.
 * The LOD pick runs per camera render from `prepare()` (hooked to scene.onBeforeRender),
 * based on the projected fan height in device pixels, so a zoomed CROWD shot gets the
 * detailed fans while the same chunk on a 240px wall tile stays cheap.
 *
 * Clothing detail (hair/beanies, sleeves vs hands, trousers + shoes, chest bands, striped
 * scarves) is per-fragment from the fan's local position, so the far boxes benefit too.
 * A handful of big waving flags in the end stands add colour (one extra draw call).
 */
export interface Crowd {
  group: THREE.Group;
  material: THREE.MeshLambertMaterial;
  /** Flag material (darkened with the stands at night). */
  flagMaterial: THREE.MeshLambertMaterial;
  maxCount: number;
  setCount(n: number): void;
  /** Pick near/far LOD per chunk for the camera about to render (viewport height in device px). */
  prepare(camera: THREE.Camera, viewportH: number): void;
  /** Smoothed per-team excitement (home, away). */
  excite: THREE.Vector2;
  uniforms: { uTime: { value: number }; uExcite: { value: THREE.Vector2 }; uIntensity: { value: number } };
  dispose(): void;
}

/** On-screen fan height (device px, nearest point of a chunk) above which the detailed mesh is used. */
const NEAR_FAN_PX = 40;
const CHUNK_SIZE = 14;

type Face = 'px' | 'nx' | 'py' | 'ny' | 'pz' | 'nz';
const FACE_ORDER: Face[] = ['px', 'nx', 'py', 'ny', 'pz', 'nz'];
/** Box without faces that are never visible from the pitch. */
function box(w: number, h: number, d: number, drop: Face[]): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  const src = g.index!.array;
  const keep: number[] = [];
  FACE_ORDER.forEach((f, i) => { if (!drop.includes(f)) for (let k = 0; k < 6; k++) keep.push(src[i * 6 + k]!); });
  g.setIndex(keep);
  g.clearGroups();
  g.deleteAttribute('uv');
  return g;
}

/** Smooth lofted tube through elliptical rings [y, rx, rz] (closed top/bottom optional). */
function loft(rings: readonly (readonly [number, number, number])[], segs: number, capTop: boolean, capBottom: boolean): THREE.BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  for (const [y, rx, rz] of rings) {
    for (let s = 0; s < segs; s++) {
      const a = (s / segs) * Math.PI * 2 + Math.PI / segs;
      pos.push(Math.sin(a) * rx, y, Math.cos(a) * rz);
    }
  }
  for (let r = 0; r < rings.length - 1; r++) {
    for (let s = 0; s < segs; s++) {
      const a = r * segs + s, b = r * segs + ((s + 1) % segs), c = a + segs, d = b + segs;
      idx.push(a, b, d, a, d, c);
    }
  }
  const cap = (ring: number, top: boolean) => {
    const [y] = rings[ring]!;
    const centre = pos.length / 3;
    pos.push(0, y + (top ? 0.012 : 0), 0);
    for (let s = 0; s < segs; s++) {
      const a = ring * segs + s, b = ring * segs + ((s + 1) % segs);
      if (top) idx.push(a, b, centre); else idx.push(b, a, centre);
    }
  };
  if (capTop) cap(rings.length - 1, true);
  if (capBottom) cap(0, false);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

// part ids: 0 torso (shirt), 1 head+neck (skin/hair), 2 legs (trousers/shoes), 3 left arm, 4 right arm, 5 scarf
function withPart(g: THREE.BufferGeometry, part: number, x = 0, y = 0, z = 0): THREE.BufferGeometry {
  g.translate(x, y, z);
  if (g.attributes.uv) g.deleteAttribute('uv');
  const n = g.attributes.position!.count;
  g.setAttribute('aPart', new THREE.Float32BufferAttribute(new Array(n).fill(part), 1));
  return g;
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  return g;
}

/** Cheap fan: fans face +z (towards the pitch). */
function farGeometry(): THREE.BufferGeometry {
  return merge([
    withPart(box(0.4, 0.56, 0.24, ['ny', 'nz']), 0, 0, 0.86, 0),
    withPart(box(0.19, 0.23, 0.21, ['ny']), 1, 0, 1.3, 0.01),
    withPart(box(0.34, 0.6, 0.22, ['py', 'ny', 'nz']), 2, 0, 0.3, 0.02),
    withPart(box(0.09, 0.56, 0.1, ['py']), 3, -0.245, 0.86, 0),
    withPart(box(0.09, 0.56, 0.1, ['py']), 4, 0.245, 0.86, 0),
  ]);
}

/** Close-range fan silhouette (~200 verts / 330 tris): shoulders, neck, round head, legs, arms, scarf. */
function nearGeometry(): THREE.BufferGeometry {
  const torso = loft([[0.56, 0.165, 0.105], [0.8, 0.175, 0.115], [1.02, 0.205, 0.125], [1.12, 0.2, 0.115], [1.17, 0.12, 0.085]], 8, true, false);
  const neck = loft([[1.15, 0.05, 0.05], [1.24, 0.045, 0.045]], 6, false, false);
  const head = new THREE.SphereGeometry(0.1, 8, 5);
  head.scale(0.95, 1.13, 1.04);
  head.translate(0, 1.32, 0.005);
  const leg = (x: number) => withPart(loft([[0.0, 0.075, 0.1], [0.3, 0.066, 0.07], [0.6, 0.085, 0.085]], 6, true, false), 2, x, 0, 0.015);
  // arm hangs from the shoulder pivot (±0.24, 1.12) down to the hand at ~0.56
  const arm = (side: number) => withPart(loft([[0.56, 0.036, 0.036], [0.86, 0.045, 0.048], [1.13, 0.056, 0.058]], 6, true, true), side < 0 ? 3 : 4, side * 0.24, 0, 0);
  const scarfRing = loft([[1.12, 0.12, 0.1], [1.19, 0.095, 0.08]], 8, false, false);
  const scarfTail = box(0.075, 0.3, 0.025, ['ny']);
  scarfTail.translate(0.05, 0.98, 0.13);
  return merge([
    withPart(torso, 0),
    withPart(merge([neck, head].map((g) => { if (g.attributes.uv) g.deleteAttribute('uv'); return g; })), 1),
    leg(-0.085), leg(0.085),
    arm(-1), arm(1),
    withPart(merge([scarfRing, scarfTail]), 5),
  ]);
}

const VERT_DECL = /* glsl */`
  attribute float aPart;
  attribute vec4 aFan;
  attribute vec4 aFan2;
  uniform float uTime;
  uniform vec2 uExcite;
  uniform float uIntensity;
  varying vec3 vLocal;
  varying float vPart;
  varying vec4 vFan;
  varying vec4 vFan2;`;

const VERT_ANIM = /* glsl */`
  vLocal = position; vPart = aPart; vFan = aFan; vFan2 = aFan2;
  float ph = aFan.x * 6.2831853;
  float temper = aFan.w;
  float ex = aFan.y < 0.5 ? uExcite.x : (aFan.y < 1.5 ? uExcite.y : max(uExcite.x, uExcite.y) * 0.45);
  ex = clamp(ex * (0.55 + 0.7 * temper), 0.0, 1.0);
  float idle = (0.25 + uIntensity) * (0.4 + temper);
  float sway = sin(uTime * (1.1 + temper) + ph) * 0.035 * idle;
  float bounce = max(0.0, sin(uTime * (5.5 + 2.0 * temper) + ph)) * (0.33 * ex + 0.03 * idle * step(0.6, temper));
  // scarf: only some fans own one; otherwise collapse it into the chest
  float acc = aFan2.y;
  if (aPart > 4.5 && !(acc > 0.5 && acc < 2.5)) transformed = vec3(0.0, 0.9, 0.0);
  if (aPart > 2.5 && aPart < 4.5) {
    float side = aPart > 3.5 ? 1.0 : -1.0;
    vec3 piv = vec3(side * 0.24, 1.12, 0.0);
    vec3 r = transformed - piv;
    float wave = sin(uTime * (7.0 + 3.0 * temper) + ph + side * 1.3);
    // a few keen fans clap / punch the air when the game is lively
    float keen = step(0.8, temper) * smoothstep(0.45, 0.8, uIntensity);
    float raise = ex * (2.55 + 0.35 * wave) + idle * 0.15 * step(0.85, temper) * (1.2 + wave) + keen * (0.9 + 0.25 * wave);
    float c1 = cos(-raise), s1 = sin(-raise);
    r = vec3(r.x, r.y * c1 - r.z * s1, r.y * s1 + r.z * c1);
    float splay = side * (0.25 * ex + 0.08) * (1.0 + 0.5 * wave * ex) - side * keen * 0.25;
    float c2 = cos(splay), s2 = sin(splay);
    r = vec3(r.x * c2 - r.y * s2, r.x * s2 + r.y * c2, r.z);
    transformed = piv + r;
  }
  if (aPart < 1.5 || aPart > 2.5) { transformed.x += sway * (transformed.y - 0.6); transformed.y += bounce; }
  else { transformed.y += bounce * 0.6; }`;

const FRAG_DECL = /* glsl */`
  uniform vec3 uHome0; uniform vec3 uHome1; uniform vec3 uAway0; uniform vec3 uAway1;
  varying vec3 vLocal;
  varying float vPart;
  varying vec4 vFan;
  varying vec4 vFan2;
  const vec3 HAIR0 = vec3(0.012, 0.009, 0.007);
  const vec3 HAIR1 = vec3(0.06, 0.03, 0.015);
  const vec3 HAIR2 = vec3(0.35, 0.22, 0.09);
  const vec3 HAIR3 = vec3(0.45, 0.45, 0.43);`;

const FRAG_COLOR = /* glsl */`
  {
    vec3 L = vLocal;
    float team = vFan.y;
    vec3 pri = team < 0.5 ? uHome0 : uAway0;
    vec3 sec = team < 0.5 ? uHome1 : uAway1;
    vec3 skin = mix(vec3(0.87, 0.55, 0.39), vec3(0.11, 0.045, 0.022), vFan.z);
    float acc = vFan2.y;            // 0 none, 1 scarf, 2 scarf + beanie, 3 beanie
    float misc = vFan2.w;
    vec3 col = diffuseColor.rgb;    // shirt (instance colour)
    if (vPart < 0.5) {
      // shirt: darker hem, team fans sometimes get a chest band in the second colour
      col *= 0.82 + 0.18 * smoothstep(0.58, 1.0, L.y);
      if (team < 1.5 && misc > 0.72 && L.y > 0.86 && L.y < 0.95) col = sec;
    } else if (vPart < 1.5) {
      col = skin;
      float h = vFan2.x;
      vec3 hair = h < 0.35 ? mix(HAIR0, HAIR1, h / 0.35) : h < 0.8 ? mix(HAIR1, HAIR2, (h - 0.35) / 0.45) : mix(HAIR2, HAIR3, (h - 0.8) / 0.2);
      float bald = step(0.93, misc);
      float hairLine = 1.385 - 0.05 * fract(misc * 7.0);
      if (bald < 0.5 && (L.y > hairLine || (L.z < -0.025 && L.y > 1.27))) col = hair;
      if (acc > 1.5 && L.y > 1.355) col = (fract(misc * 13.0) > 0.5 ? sec : pri) * (L.y > 1.37 ? 1.0 : 0.75);
      if (L.y < 1.235) col *= 0.8; // neck shadow under the chin
    } else if (vPart < 2.5) {
      float t = vFan2.z;
      vec3 trousers = t < 0.4 ? vec3(0.035, 0.05, 0.09) : t < 0.7 ? vec3(0.015) : t < 0.88 ? vec3(0.1, 0.1, 0.11) : vec3(0.28, 0.22, 0.14);
      col = L.y < 0.075 ? (misc > 0.5 ? vec3(0.65) : vec3(0.02)) : trousers;
    } else if (vPart < 4.5) {
      float sleeve = misc > 0.3 ? 0.66 : 0.92;   // long vs short sleeves
      if (L.y < sleeve) col = skin; else col *= 0.92;
    } else {
      col = fract(L.y * 6.5 + L.x * 2.0) > 0.62 ? sec : pri;
    }
    diffuseColor.rgb = col;
  }`;

// ------------------------------------------------------------------ flags
const FLAG_VERT_DECL = /* glsl */`
  attribute vec4 aFlag; // phase, team, pattern, size
  uniform float uTime; uniform vec2 uExcite; uniform float uIntensity;
  varying vec2 vFlagUv; varying vec4 vFlag;`;
const FLAG_VERT = /* glsl */`
  vFlagUv = uv; vFlag = aFlag;
  float ex = aFlag.y < 0.5 ? uExcite.x : uExcite.y;
  float energy = 0.35 + 0.45 * uIntensity + 0.9 * ex;
  // cloth (uv.x > 0) waves; the pole (uv.x < 0) sways as the fan swings it
  float u = max(uv.x, 0.0);
  float ph = aFlag.x * 6.2831853;
  float swing = sin(uTime * (1.6 + 1.6 * energy) + ph) * 0.28 * energy;
  transformed.z += sin(uTime * (5.0 + 3.0 * energy) - u * 5.0 + ph) * 0.16 * u * (0.4 + energy);
  transformed.y -= u * u * 0.25 * (1.0 - min(1.0, energy));
  // swing about the base of the pole
  float c = cos(swing), s = sin(swing);
  transformed.xy = vec2(transformed.x * c - transformed.y * s, transformed.x * s + transformed.y * c);
  transformed *= aFlag.w;`;
const FLAG_FRAG_DECL = /* glsl */`
  uniform vec3 uHome0; uniform vec3 uHome1; uniform vec3 uAway0; uniform vec3 uAway1;
  varying vec2 vFlagUv; varying vec4 vFlag;`;
const FLAG_FRAG = /* glsl */`
  {
    vec3 pri = vFlag.y < 0.5 ? uHome0 : uAway0;
    vec3 sec = vFlag.y < 0.5 ? uHome1 : uAway1;
    vec2 q = vFlagUv;
    float p = vFlag.z;
    float m;
    if (p < 0.25) m = step(0.5, q.x);                               // halves
    else if (p < 0.5) m = step(0.5, fract(q.y * 1.5));              // three bars
    else if (p < 0.75) m = step(0.5, fract((q.x + q.y) * 1.5));     // diagonal
    else m = step(0.22, abs(q.y - 0.5)) ;                           // centre band
    vec3 c = mix(pri, sec, m);
    if (q.x < 0.0) c = vec3(0.55);                                  // pole
    diffuseColor.rgb = c;
  }`;

function flagGeometry(): THREE.BufferGeometry {
  // cloth 1.6 x 1.05 (subdivided), top at the pole tip; pole 2.4 tall. Base at origin, cloth along +x.
  const cloth = new THREE.PlaneGeometry(1.6, 1.05, 8, 2);
  cloth.translate(0.8, 2.4 - 0.525, 0);
  const uv = cloth.attributes.uv!;
  for (let i = 0; i < uv.count; i++) uv.setX(i, Math.max(0.001, uv.getX(i)));
  const pole = new THREE.CylinderGeometry(0.02, 0.02, 2.4, 4, 1, true);
  pole.translate(0, 1.2, 0);
  const puv = pole.attributes.uv!;
  for (let i = 0; i < puv.count; i++) puv.setXY(i, -1, 0);
  const g = mergeGeometries([cloth, pole], false)!;
  cloth.dispose(); pole.dispose();
  return g;
}

export function buildCrowd(config: MatchConfig, seats: Float32Array, seatCount: number): Crowd {
  const max = seatCount;
  const rnd = prng(config.seed ^ 0xc0ffee);
  const fan = new Float32Array(max * 4);
  const fan2 = new Float32Array(max * 4);
  const mats = new Float32Array(max * 16);
  const cols = new Float32Array(max * 3);
  const pos = new Float32Array(max * 3);

  // Random permutation so that lowering the instance count thins uniformly.
  const order = new Uint32Array(max);
  for (let i = 0; i < max; i++) order[i] = i;
  for (let i = max - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); const t = order[i]!; order[i] = order[j]!; order[j] = t; }

  const home = [new THREE.Color(config.home.primaryColor), new THREE.Color(config.home.secondaryColor)];
  const away = [new THREE.Color(config.away.primaryColor), new THREE.Color(config.away.secondaryColor)];
  const neutral = ['#1d2533', '#2b2b2b', '#4a4f57', '#6b6f75', '#d8d8d8', '#2e3e5c', '#5c3b2a', '#8a8f96', '#3f5a3a', '#7a2230', '#b8a27a', '#20304a'].map((h) => new THREE.Color(h));
  const white = new THREE.Color('#e9e9e9'), black = new THREE.Color('#151515');
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), s = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  const c = new THREE.Color();
  const flagSeats: number[] = [];
  for (let k = 0; k < max; k++) {
    const i = order[k]!;
    const x = seats[i * 5]!, y = seats[i * 5 + 1]!, z = seats[i * 5 + 2]!, ang = seats[i * 5 + 3]!, code = seats[i * 5 + 4]!;
    const sc = 0.92 + rnd() * 0.16;
    p.set(x + (rnd() - 0.5) * 0.08, y, z);
    q.setFromAxisAngle(up, ang + (rnd() - 0.5) * 0.3);
    s.set(sc, sc * (0.94 + rnd() * 0.1), sc);
    m.compose(p, q, s);
    m.toArray(mats, k * 16);
    pos[k * 3] = p.x; pos[k * 3 + 1] = p.y; pos[k * 3 + 2] = p.z;
    // allegiance by section
    let pHome = 0.68, pAway = 0.06;
    if (code === 0) { pHome = 0.88; pAway = 0.02; }
    else if (code === 1) { if (z < 6) { pHome = 0.06; pAway = 0.86; } else { pHome = 0.7; pAway = 0.08; } }
    const r = rnd();
    let team = 2;
    if (r < pHome) team = 0; else if (r < pHome + pAway) team = 1;
    if (team === 2) c.copy(neutral[Math.floor(rnd() * neutral.length)]!);
    else {
      const pal = team === 0 ? home : away;
      const t = rnd();
      c.copy(t < 0.58 ? pal[0]! : t < 0.82 ? pal[1]! : t < 0.9 ? white : t < 0.95 ? black : neutral[Math.floor(rnd() * neutral.length)]!);
    }
    c.multiplyScalar(0.82 + rnd() * 0.3);
    c.toArray(cols, k * 3);
    fan[k * 4] = rnd();
    fan[k * 4 + 1] = team;
    fan[k * 4 + 2] = Math.pow(rnd(), 1.3);
    fan[k * 4 + 3] = rnd();
    // hair tone, accessory (team fans: scarves/beanies), trousers, misc
    const a = rnd();
    let acc = 0;
    if (team < 2) acc = a < 0.17 ? 1 : a < 0.23 ? 2 : a < 0.33 ? 3 : 0;
    else acc = a < 0.06 ? 3 : 0;
    fan2[k * 4] = rnd();
    fan2[k * 4 + 1] = acc;
    fan2[k * 4 + 2] = rnd();
    fan2[k * 4 + 3] = rnd();
    // flag bearers: front-half rows of the two end stands, team fans only
    if ((code === 0 || (code === 1 && z < 6)) && team < 2 && rnd() < 0.006 && y < 20) flagSeats.push(k);
  }

  // ---------------------------------------------------------------- chunks
  const cellOf = (k: number) => {
    const tier = pos[k * 3 + 1]! > 11 ? 1 : 0;
    const cx = Math.floor(pos[k * 3]! / CHUNK_SIZE), cz = Math.floor(pos[k * 3 + 2]! / CHUNK_SIZE);
    return `${tier}:${cx}:${cz}`;
  };
  const buckets = new Map<string, number[]>();
  for (let k = 0; k < max; k++) {
    const key = cellOf(k);
    let b = buckets.get(key);
    if (!b) { b = []; buckets.set(key, b); }
    b.push(k);
  }

  const material = new THREE.MeshLambertMaterial({ color: 0xffffff });
  const uniforms = { uTime: { value: 0 }, uExcite: { value: new THREE.Vector2() }, uIntensity: { value: 0.3 } };
  const teamUniforms = {
    uHome0: { value: home[0]!.clone() }, uHome1: { value: home[1]!.clone() },
    uAway0: { value: away[0]!.clone() }, uAway1: { value: away[1]!.clone() },
  };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms, teamUniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_DECL}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${VERT_ANIM}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_DECL}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${FRAG_COLOR}`);
  };
  material.customProgramCacheKey = () => 'sd-crowd-v2';

  const farGeo = farGeometry();
  const nearGeo = nearGeometry();
  const group = new THREE.Group();
  group.name = 'crowd';
  interface Chunk { near: THREE.InstancedMesh; far: THREE.InstancedMesh; n: number; centre: THREE.Vector3; radius: number }
  const chunks: Chunk[] = [];
  const disposables: { dispose(): void }[] = [];
  const mkGeo = (base: THREE.BufferGeometry, aFan: THREE.InstancedBufferAttribute, aFan2: THREE.InstancedBufferAttribute) => {
    const g = new THREE.BufferGeometry();
    for (const name of Object.keys(base.attributes)) g.setAttribute(name, base.attributes[name]!);
    g.setIndex(base.index);
    g.setAttribute('aFan', aFan);
    g.setAttribute('aFan2', aFan2);
    return g;
  };
  for (const list of buckets.values()) {
    const n = list.length;
    const im = new Float32Array(n * 16), ic = new Float32Array(n * 3), f1 = new Float32Array(n * 4), f2 = new Float32Array(n * 4);
    const box3 = new THREE.Box3();
    list.forEach((k, j) => {
      im.set(mats.subarray(k * 16, k * 16 + 16), j * 16);
      ic.set(cols.subarray(k * 3, k * 3 + 3), j * 3);
      f1.set(fan.subarray(k * 4, k * 4 + 4), j * 4);
      f2.set(fan2.subarray(k * 4, k * 4 + 4), j * 4);
      box3.expandByPoint(p.set(pos[k * 3]!, pos[k * 3 + 1]!, pos[k * 3 + 2]!));
    });
    box3.max.y += 2.2; // fans stand ~1.5 m, arms up + bounce
    const sphere = box3.getBoundingSphere(new THREE.Sphere());
    sphere.radius += 0.6;
    const matAttr = new THREE.InstancedBufferAttribute(im, 16);
    const colAttr = new THREE.InstancedBufferAttribute(ic, 3);
    const a1 = new THREE.InstancedBufferAttribute(f1, 4), a2 = new THREE.InstancedBufferAttribute(f2, 4);
    const make = (base: THREE.BufferGeometry, lod: string) => {
      const g = mkGeo(base, a1, a2);
      g.boundingSphere = sphere.clone();
      const mesh = new THREE.InstancedMesh(g, material, n);
      mesh.instanceMatrix = matAttr;
      mesh.instanceColor = colAttr;
      mesh.boundingSphere = sphere.clone();
      mesh.name = `crowd-${lod}`;
      mesh.matrixAutoUpdate = false;
      mesh.receiveShadow = true;
      group.add(mesh);
      disposables.push(g);
      return mesh;
    };
    const far = make(farGeo, 'far');
    const near = make(nearGeo, 'near');
    near.visible = false;
    chunks.push({ near, far, n, centre: sphere.center.clone(), radius: sphere.radius });
  }

  // ---------------------------------------------------------------- flags
  const flagGeo = flagGeometry();
  const nf = flagSeats.length;
  const flagData = new Float32Array(Math.max(1, nf) * 4);
  const flagMaterial = new THREE.MeshLambertMaterial({ color: 0xffffff, side: THREE.DoubleSide });
  const flags = new THREE.InstancedMesh(flagGeo, flagMaterial, Math.max(1, nf));
  flags.name = 'crowd-flags';
  flags.count = nf;
  flags.matrixAutoUpdate = false;
  flagSeats.forEach((k, j) => {
    m.fromArray(mats, k * 16);
    m.decompose(p, q, s);
    // pole held in front of the fan at hand height, cloth flies sideways
    const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(q);
    p.addScaledVector(fwd, 0.25); p.y += 1.0;
    s.setScalar(1);
    m.compose(p, q, s);
    flags.setMatrixAt(j, m);
    flagData[j * 4] = rnd();
    flagData[j * 4 + 1] = fan[k * 4 + 1]!;
    flagData[j * 4 + 2] = rnd();
    flagData[j * 4 + 3] = 0.9 + rnd() * 0.5;
  });
  flagGeo.setAttribute('aFlag', new THREE.InstancedBufferAttribute(flagData, 4));
  flags.computeBoundingSphere();
  if (flags.boundingSphere) flags.boundingSphere.radius += 4;
  flagMaterial.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms, teamUniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${FLAG_VERT_DECL}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${FLAG_VERT}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FLAG_FRAG_DECL}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${FLAG_FRAG}`);
  };
  flagMaterial.customProgramCacheKey = () => 'sd-crowd-flags-v1';
  if (nf > 0) group.add(flags);

  // ---------------------------------------------------------------- LOD
  let scale = 1;
  const camPos = new THREE.Vector3();
  const crowd: Crowd = {
    group,
    material,
    flagMaterial,
    maxCount: max,
    setCount(n: number) {
      scale = Math.max(0, Math.min(1, n / Math.max(1, max)));
      for (const ch of chunks) { const cnt = Math.floor(ch.n * scale); ch.near.count = cnt; ch.far.count = cnt; }
      flags.count = scale < 0.5 ? 0 : nf;
    },
    prepare(camera, viewportH) {
      const cam = camera as THREE.PerspectiveCamera;
      camPos.setFromMatrixPosition(cam.matrixWorld);
      const tanHalf = Math.tan(THREE.MathUtils.degToRad((cam.fov ?? 50) / cam.zoom) / 2);
      // px of a 1.5 m fan at distance d: 1.5 * H / (2 d tanHalf)
      const k = (1.5 * viewportH) / (2 * Math.max(1e-4, tanHalf));
      for (const ch of chunks) {
        const d = Math.max(0.5, camPos.distanceTo(ch.centre) - ch.radius);
        const near = k / d > NEAR_FAN_PX && scale > 0.5;
        ch.near.visible = near;
        ch.far.visible = !near;
      }
    },
    excite: uniforms.uExcite.value,
    uniforms,
    dispose() {
      for (const d of disposables) d.dispose();
      farGeo.dispose(); nearGeo.dispose(); flagGeo.dispose();
      material.dispose(); flagMaterial.dispose(); flags.dispose();
    },
  };
  return crowd;
}
